<template>
    <div>
        <div v-if="!isViewer" class="mb-4 flex items-center space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Dashboard</h1>
        </div>

        <div v-if="!isPartner" class="pt-4 grid gap-6 h-full" :class="isViewer ? 'grid-cols-4 gap-8' : 'grid-cols-1 gap-6'">
            <div class="h-full flex flex-col space-y-12" :class="isViewer ? 'col-span-3' : 'col-span-1'">
                <div>
                    <div class="flex items-start justify-between">
                        <div>
                            <h3 class="text-lg font-semibold tracking-tight leading-6 text-gray-900">Overview</h3>
                            <p v-if="statsError" class="mt-1 text-sm text-red-600">
                                Couldn't load stats ·
                                <button type="button" class="font-medium underline" @click="fetchStats()">Retry</button>
                            </p>
                            <p v-else class="mt-1 text-sm text-gray-500" :class="{ invisible: !statsWindow }">{{ statsWindowLabel }}</p>
                        </div>
                        <USelectMenu
                            v-model="range"
                            :options="rangeOptions"
                            value-attribute="value"
                            option-attribute="label"
                            class="w-40"
                        />
                    </div>

                    <div v-if="!callStats" class="mt-4 grid grid-cols-4 divide-x divide-gray-200 overflow-hidden rounded-lg bg-white shadow">
                        <div v-for="i in 4" :key="i" class="p-6">
                            <dt class="w-1/2 h-6 bg-gray-200 animate-pulse rounded-full"></dt>
                            <dd class="mt-2 w-full h-7 bg-gray-200 animate-pulse rounded-full"></dd>
                        </div>
                    </div>
                    <div v-else class="mt-4 grid grid-cols-4 divide-x divide-gray-200 overflow-hidden rounded-lg bg-white ring-1 ring-black/5 shadow">
                        <div v-for="card in totalCards" :key="card.name" class="p-6">
                            <dt class="text-base font-medium text-gray-900 tracking-tight">{{ card.name }}</dt>
                            <dd class="mt-1 flex items-baseline justify-between">
                                <div class="flex items-baseline text-2xl font-semibold text-primary">
                                    {{ card.value }}
                                    <span class="ml-2 text-sm font-normal text-gray-400">vs. {{ card.previousValue }}</span>
                                </div>
                                <span
                                    v-if="card.delta"
                                    class="inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium"
                                    :class="TONE_BADGE[card.delta.tone]"
                                >
                                    {{ card.delta.label }}
                                </span>
                            </dd>
                        </div>
                    </div>

                    <h3 class="mt-8 text-lg font-semibold tracking-tight leading-6 text-gray-900">
                        Call handling <span class="font-normal text-gray-400">· App calls</span>
                    </h3>
                    <DashboardHandlingTable
                        v-if="callStats"
                        class="mt-4"
                        :current="callStats.current.handling"
                        :previous="callStats.previous.handling.all"
                    />
                    <div v-else class="mt-4 h-64 rounded-lg bg-gray-200/60 animate-pulse" />
                </div>
                <div class="flex flex-col" :class="{ 'flex-1': isViewer }">
                    <h3 class="mb-4 text-lg flex items-center space-x-2 font-medium leading-6 text-gray-900">
                        <span class="font-semibold tracking-tight">Active Users</span>
                        <span :class="{ invisible: loading }" class="px-2 py-1 bg-gray-200 text-sm font-semibold rounded-lg">
                            {{ totalUsers }}
                        </span>
                        <span class="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            <span class="size-2 rounded-full bg-green-500 animate-pulse" />
                            Live
                        </span>
                    </h3>
                    <div class="flex-1 overflow-hidden rounded-lg bg-gray-300/20 shadow-inner">
                        <dl
                            v-if="loading"
                            class="h-full w-full flex flex-col py-6 space-y-2 items-center justify-center"
                        >
                            <UIcon name="i-ph-user-duotone" class="size-7 text-gray-300" />
                            <span class="inline-flex font-medium text-gray-300 text-sm">Loading users...</span>
                        </dl>
                        <dl
                            v-else-if="!users?.length"
                            class="h-full w-full flex flex-col space-y-2 items-center justify-center"
                        >
                            <UIcon name="i-ph-user-minus-duotone" class="h-8 w-8 text-gray-300" />
                            <span class="inline-flex font-medium text-gray-300 text-sm">No active users.</span>
                        </dl>
                        <dl v-else class="grid grid-cols-3 gap-4 p-4">
                            <div v-for="user in users" :key="user.id" class="p-5 rounded-lg bg-white shadow">
                                <div class="flex items-center space-x-4">
                                    <div class="flex-shrink-0 flex items-center justify-center p-2 rounded-md ring-1 ring-black/10 bg-gray-100">
                                        <UIcon name="i-ph-user-duotone" class="h-5 w-5 text-gray-300" />
                                    </div>
                                    <div class="flex flex-1 items-center justify-between space-x-2">
                                        <div>
                                            <p class="leading-5 font-medium text-gray-900">
                                                {{ user.name }}
                                            </p>
                                            <p v-if="user.metadata?.extension" class="text-xs font-medium text-gray-600">
                                                <span class="leading-none text-base text-green-500">&bull;</span> Extension: {{ user.metadata.extension }}
                                            </p>
                                        </div>
                                        <BadgeUserRole v-if="user.role" :role="user.role" />
                                    </div>
                                </div>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
            <div v-if="isViewer" class="col-span-1 flex flex-col">
                <h3 class="mb-4 text-lg flex items-center space-x-2 font-medium leading-6 text-gray-900">
                    <span class="font-semibold tracking-tight">Active Calls</span>
                    <span :class="{ invisible: loading }" class="px-2 py-1 bg-gray-200 text-sm font-semibold rounded-lg">
                        {{ totalActiveCalls }}
                    </span>
                    <span class="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                        <span class="size-2 rounded-full bg-green-500 animate-pulse" />
                        Live
                    </span>
                </h3>
                <div class="flex-1 overflow-hidden rounded-lg bg-gray-300/20 shadow-inner">
                    <dl
                        v-if="loading"
                        class="h-full w-full flex flex-col space-y-2 items-center justify-center"
                    >
                        <UIcon name="i-ph-user-duotone" class="h-8 w-8 text-gray-300" />
                        <span class="inline-flex font-medium text-gray-300 text-sm">Loading calls...</span>
                    </dl>
                    <dl
                        v-else-if="!activeCalls?.length"
                        class="h-full w-full flex flex-col space-y-2 items-center justify-center"
                    >
                        <UIcon name="i-ph-phone-x-duotone" class="h-8 w-8 text-gray-300" />
                        <span class="inline-flex font-medium text-gray-300 text-sm">No active calls.</span>
                    </dl>
                    <dl v-else class="gap-4 p-4">
                        <div v-for="call in activeCalls" class="flex items-center space-x-4 p-5 rounded-lg bg-white shadow">
                            <Avatar
                                size="md"
                                class="shrink-0 border border-white/15"
                                rounded="rounded-full"
                                :chip-color="
                                    call.status === CallStatus.new 
                                    ? 'red'
                                    : call.status === CallStatus.active 
                                    ? 'blue' 
                                    : call.status === CallStatus.onHold 
                                    ? 'amber' 
                                    : undefined
                                "
                                :ui="{ size: { sm: 'text-xs' } }"
                                :path="call.customer?.avatar" 
                                :placeholder="[call.customer?.first_name, call.customer?.last_name].filter(Boolean).join(' ')" 
                            />
                            <div class="flex-1 flex flex-col overflow-hidden leading-none">
                                <h2 class="font-medium text-base text-gray-800 truncate">
                                    {{ [call.customer?.first_name, call.customer?.middle_name, call.customer?.last_name].filter(Boolean).join(' ') }}
                                </h2>
                                <span class="text-sm text-gray-400 capitalize">
                                    {{ [call.customer?.gender, age(call.customer?.birth_date), call.customer?.blood_type].filter(Boolean).join(' · ') }}
                                </span>
                            </div>
                        </div>
                    </dl>
                </div>
            </div>
        </div>

        <div v-if="isPartner" class="pt-4 grid grid-cols-1 gap-6 h-full">
            <h2 class="text-lg font-medium text-gray-700">Welcome, {{ auth.user?.name }}</h2>

            <div>
                <UButton
                    label="Bulk Activation"
                    @click="overlays.bulkActivate = true" 
                    icon="i-ph-shield-plus-bold"
                />
            </div>

            <OverlayCredentialBulkActivation v-model:open="overlays.bulkActivate" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { type Call, CallStatus } from '~/types/call'
import { UserRole, type User } from '~/types/user'
import type { DashboardCallStats } from '~/types/dashboard'
import {
    DASHBOARD_RANGES, TONE_BADGE, countDelta, createLatestGuard, parseRange, rangeWindow, windowLabel,
    type StatsWindow,
} from '~/utils/dashboardStats'

definePageMeta({ layout: 'default', middleware: 'auth' })
useHead({ title: 'Dashboard' })

const auth = useAuthStore()
const client = useSupabaseClient()
const route = useRoute()

const { age } = useNumber()

const loading = ref<boolean>(false)
const liveInterval = ref<ReturnType<typeof setInterval>>()
const statsInterval = ref<ReturnType<typeof setInterval>>()
const users = ref<Partial<User>[]>([])
const activeCalls = ref<Partial<Call>[]>([])
const callStats = ref<DashboardCallStats | null>(null)
const statsWindow = ref<StatsWindow | null>(null)
const statsError = ref<boolean>(false)
const statsGuard = createLatestGuard()

const overlays = reactive({
    bulkActivate: false,
})

const rangeOptions = DASHBOARD_RANGES.map(({ value, label }) => ({ value, label }))

const range = computed({
    get: () => parseRange(route.query.range),
    set: (value) => navigateTo({ query: { ...route.query, range: value } }, { replace: true }),
})
const rangeConfig = computed(() => DASHBOARD_RANGES.find((r) => r.value === range.value)!)

const isViewer = computed(() => auth.user?.role === UserRole.viewer)
const isPartner = computed(() => auth.user?.role === UserRole.partner)
const totalUsers = computed(() => users.value.length)
const totalActiveCalls = computed(() => activeCalls.value.length)

const statsWindowLabel = computed(() => (statsWindow.value ? windowLabel(statsWindow.value) : ''))

const totalCards = computed(() => {
    if (!callStats.value) return []
    const current = callStats.value.current.totals
    const previous = callStats.value.previous.totals
    return [
        { name: 'Total calls', value: current.total, previousValue: previous.total, delta: countDelta(current.total, previous.total, 'percent', 'neutral') },
        { name: 'App calls', value: current.app, previousValue: previous.app, delta: countDelta(current.app, previous.app, 'percent', 'neutral') },
        { name: 'External calls', value: current.external, previousValue: previous.external, delta: countDelta(current.external, previous.external, 'percent', 'neutral') },
        { name: 'Timed-out calls', value: current.timed_out, previousValue: previous.timed_out, delta: countDelta(current.timed_out, previous.timed_out, 'absolute', 'lower') },
    ]
})

onMounted(async () => {
    if (isPartner.value) return
    liveInterval.value = setInterval(fetchLive, 30_000)
    scheduleStats()
    fetchStats()
    loading.value = true
    await fetchLive()
    loading.value = false
})

onUnmounted(() => {
    clearInterval(liveInterval.value)
    clearInterval(statsInterval.value)
})

watch(range, () => {
    if (isPartner.value) return
    fetchStats()
    scheduleStats()
})

function scheduleStats() {
    clearInterval(statsInterval.value)
    statsInterval.value = setInterval(fetchStats, rangeConfig.value.refreshMs)
}

async function fetchLive() {
    await Promise.all([fetchUsers(), fetchCalls()])
}

async function fetchStats() {
    const request = statsGuard.next()
    const window = { ...rangeWindow(range.value), range: range.value }
    const { data, error } = await client.rpc('dashboard_call_stats', {
        p_from: window.from.toISOString(),
        p_to: window.to.toISOString(),
    } as never)
    if (!statsGuard.isLatest(request)) return
    statsError.value = !!error || !data
    if (statsError.value) return
    callStats.value = data as unknown as DashboardCallStats
    statsWindow.value = window
}

async function fetchUsers() {
    const { data } = await client.from('users')
        .select('id, name, role, metadata')
        .not('login_at', 'is', null)
        .is('logout_at', null)
        .in('role', [UserRole.medicalDirector, UserRole.doctor, UserRole.operator])

    if (data) {
        users.value = data
    }
}

async function fetchCalls() {
    const { data } = await client.from('calls')
        .select(`
            id, number, room_sid, status, created_at,
            user:users!calls_user_id_fkey (
                id, name, role
            ), 
            customer:customers (
                id, first_name, middle_name, last_name, avatar, birth_date, gender, blood_type
            ), 
            address:addresses (
                id, city, region, country
            )
        `)
        .in('status', [CallStatus.new, CallStatus.active, CallStatus.onHold])

    if (data) {
        activeCalls.value = data as unknown as Call[]
    }
}
</script>
